<template>
  <div class="criteria">
    <div class="section-head"><h2>Acceptance criteria</h2></div>
    <p class="section-lede">
      These settings are stored with the saved query and written to the workbook's
      <strong>Config</strong> sheet, one setting per row.
    </p>

    <div class="config-grid">
      <div class="threshold-card">
        <div class="threshold-card__head">Thresholds</div>
        <div class="threshold-card__body">
          <div class="threshold-field">
            <label for="rpd-at">Acceptance threshold (AT)</label>
            <div class="threshold-field__row">
              <v-text-field
                id="rpd-at"
                :value="thresholds.acceptanceThreshold"
                type="number"
                min="0"
                step="0.1"
                hide-details
                @input="setThreshold('acceptanceThreshold', $event)"
              />
              <span class="unit">%</span>
            </div>
            <p class="explain">
              Default {{ defaults.acceptanceThreshold }}. A row fails only when RPD is
              <em>strictly greater</em> than AT; an RPD equal to AT passes.
            </p>
          </div>

          <div class="threshold-field">
            <label for="rpd-dlmt">Detection-limit multiplier (DLMT)</label>
            <div class="threshold-field__row">
              <v-text-field
                id="rpd-dlmt"
                :value="thresholds.detectionLimitMultiplier"
                type="number"
                min="0"
                step="0.1"
                hide-details
                @input="setThreshold('detectionLimitMultiplier', $event)"
              />
              <span class="unit">×</span>
            </div>
            <p class="explain">
              Default {{ defaults.detectionLimitMultiplier }}. A result below
              <code>DLMT × DetectionLimit</code> is flagged <code>BelowDLMT</code>.
            </p>
          </div>

          <div class="threshold-field">
            <p class="formula" aria-label="RPD formula">RPD = |P − D| / ((P + D) / 2) × 100%</p>
            <p class="explain">
              Comparison uses the <strong>unrounded</strong> RPD. The displayed value rounds to one
              decimal place, so a row can display <code>20.0</code> and still fail.
            </p>
          </div>
        </div>
      </div>

      <div class="exclusions">
        <div class="exclusions__head">
          <h3>Excluded analytes</h3>
          <span class="chip chip--success">
            <i class="material-icons" aria-hidden="true">playlist_add_check</i>
            Permissive by default
          </span>
          <span class="field-note">{{ excludedCount }} of {{ catalogue.length }} excluded</span>
        </div>

        <RpdVerdictBand :band="permissiveNote" />

        <div class="picker">
          <div class="picker__chips" aria-label="Analytes excluded from the calculation">
            <span v-for="analyte in excludedAnalytes" :key="analyte.id" class="chip">
              {{ analyte.name }}
              <button type="button" class="chip__close" :aria-label="'Re-include ' + analyte.name" @click="toggle(analyte.id)">
                <i class="material-icons" aria-hidden="true">close</i>
              </button>
            </span>
          </div>
          <p v-if="!excludedCount" class="picker__chips-empty">
            Nothing is excluded yet. Every analyte the two samples share is calculated.
          </p>

          <div class="picker__search">
            <i class="material-icons" aria-hidden="true">search</i>
            <label class="sr-only" for="rpd-excl-search">Search analytes to exclude</label>
            <v-text-field
              id="rpd-excl-search"
              :value="search"
              type="search"
              :placeholder="'Search the tenant\'s ' + catalogue.length + ' analytes…'"
              hide-details
              @input="search = $event"
            />
            <span class="field-note">Showing {{ matches.length }} of {{ catalogue.length }}</span>
          </div>

          <v-progress-linear v-if="loading" :height="2" color="info" indeterminate />

          <ul class="picker__options" role="listbox" aria-multiselectable="true" aria-label="Analytes in this tenant">
            <li
              v-for="analyte in matches"
              :key="analyte.id"
              class="picker__option"
              role="option"
              tabindex="0"
              :aria-selected="isExcluded(analyte.id) ? 'true' : 'false'"
              @click="toggle(analyte.id)"
              @keydown.enter.prevent="toggle(analyte.id)"
              @keydown.space.prevent="toggle(analyte.id)"
            >
              <i class="material-icons" aria-hidden="true">
                {{ isExcluded(analyte.id) ? 'check_box' : 'check_box_outline_blank' }}
              </i>
              <span class="picker__option-name">
                {{ analyte.name }}
                <span v-if="analyte.fraction && analyte.fraction !== '-'" class="sub">{{ analyte.fraction }}</span>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import RpdVerdictBand from './RpdVerdictBand'
  import {
    RPD_DEFAULT_ACCEPTANCE_THRESHOLD,
    RPD_DEFAULT_DETECTION_LIMIT_MULTIPLIER,
  } from '@/helpers/rpd'

  /*
   * Thresholds and exclusions.
   *
   * Exclusions are the one place the design is deliberately permissive: an analyte is calculated
   * unless it is explicitly turned off, so an analyte that newly appears in the data can never be
   * silently dropped. The picker therefore starts empty and reads as a subtraction from the whole
   * catalogue rather than as a selection from a list.
   */
  export default {
    name: 'RpdCriteriaExclusions',

    components: { RpdVerdictBand },

    props: {
      thresholds: {
        type: Object,
        required: true,
      },
      excludedAnalyteIds: {
        type: Array,
        default: () => [],
      },
      catalogue: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        search: '',
        defaults: {
          acceptanceThreshold: RPD_DEFAULT_ACCEPTANCE_THRESHOLD,
          detectionLimitMultiplier: RPD_DEFAULT_DETECTION_LIMIT_MULTIPLIER,
        },
        permissiveNote: {
          kind: 'idle',
          icon: 'info',
          title: 'Analytes are included unless you turn them off',
          detail: 'Select as many analytes as you need to exclude. Anything you leave unselected is calculated, so an analyte that newly appears in the data is never silently dropped.',
        },
      }
    },

    computed: {
      excludedCount() {
        return this.excludedAnalyteIds.length
      },

      excludedAnalytes() {
        return this.catalogue.filter((analyte) => this.isExcluded(analyte.id))
      },

      matches() {
        const needle = this.search.trim().toLowerCase()
        if (!needle) return this.catalogue
        return this.catalogue.filter((analyte) => analyte.name.toLowerCase().indexOf(needle) !== -1)
      },
    },

    methods: {
      isExcluded(id) {
        return this.excludedAnalyteIds.indexOf(id) !== -1
      },

      toggle(id) {
        const next = this.isExcluded(id)
          ? this.excludedAnalyteIds.filter((value) => value !== id)
          : [...this.excludedAnalyteIds, id]

        this.$emit('update:excludedAnalyteIds', next)
      },

      /* The empty string is a real state while typing, so it is passed through as '' and only
         the payload builder turns it back into the default. */
      setThreshold(field, value) {
        this.$emit('update:thresholds', { ...this.thresholds, [field]: value })
      },
    },
  }
</script>
